import React, { useState, useEffect, useCallback, useRef } from 'react'
import { cn } from '@utils'
import { useSessionStore } from '@stores'
import { useUIStore } from '@stores'
import { authService } from '@services/authService'
import { cartService } from '@services/cartService'
import StepTitle from '@/components/StepTitle'
import VerificationField from './VerificationField'
import { ArrowRight } from 'lucide-react'
import { useToast } from '@/hooks/useToast'
import { SessionStatus, MFAStatus, MFAMethod, HTTP_STATUS } from '@constants'
import { CustomerVerifyMessages } from '@/constants/loadingMessages'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import { stopMouseflowRecording } from '@/utils/mouseflow'

// Verification method constants - matching VerificationField component
enum VerificationMethod {
  FullName = 1,
  LastName = 2,
  Street = 3,
  Zip = 4,
  Address = 5,
  BillingStreet = 6,
  BillingZip = 7,
  BillingAddress = 8,
  Phone = 1000,
  PartialPhone = 1001,
  Email = 1002,
  PartialEmail = 1003,
}

interface CustomerVerifyProps {
  getCart?: boolean
  useGlobalLoading?: boolean
  onDone?: () => void
  onBack?: () => void
  onFail?: (message: string) => void
  onVerifying?: (verifying: boolean) => void
}

interface VerificationValues {
  first_name: string
  last_name: string
  address: string
  city: string
  state: string
  zip: string
  phone: string
  email: string
  country: string
}

interface MFAChoice {
  value: number
  label: string
}

// Pure display helpers — module scope keeps them stable across renders.
const maskName = (name: string): string => {
  if (name.length <= 5) {
    return name.slice(0, 1) + '*'.repeat(name.length - 2) + name.slice(-1)
  }
  return name.slice(0, 2) + '*'.repeat(name.length - 4) + name.slice(-2)
}

const obfuscateEmail = (email: string): string => {
  const parts = email.split('@')
  if (parts.length !== 2) {
    return email
  }

  const name = parts[0]
  const domain = parts[1]
  const obfuscatedName = maskName(name)
  const domainParts = domain.split('.')

  if (domainParts.length < 2) {
    return obfuscatedName + '@' + domain
  }

  const domainName = domainParts.slice(0, -1).join('.')
  const tld = domainParts[domainParts.length - 1]
  const obfuscatedDomainName = maskName(domainName)

  return obfuscatedName + '@' + obfuscatedDomainName + '.' + tld
}

const CustomerVerify = ({
  getCart = true,
  useGlobalLoading = true,
  onDone,
  onBack,
  onFail,
  onVerifying,
}: CustomerVerifyProps) => {
  const session = useSessionStore(state => state.session)
  const setMatchVerifyError = useSessionStore(state => state.setMatchVerifyError)
  const matchVerifyErrors = useSessionStore(state => state.isMatchVerifyError)
  const setCriticalError = useUIStore(state => state.setCriticalError)
  const setLoading = useUIStore(state => state.setLoading)

  // Form state
  const [values, setValues] = useState<VerificationValues>({
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    country: '',
  })

  // Placeholder state
  const [placeholders] = useState<Record<string, string>>({
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    country: '',
  })

  const toast = useToast()

  // MFA state
  const [showMFAChoices, setShowMFAChoices] = useState(false)
  const [selectedMFAMethod, setSelectedMFAMethod] = useState<number | null>(null)
  const [MFAChoices, setMFAChoices] = useState<MFAChoice[]>([])
  const [mfaDigits, setMfaDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [mfaError, setMfaError] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Refs for MFA inputs
  const mfaInputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Computed values
  const isMFA =
    session && (session as any).status === SessionStatus.Verified && (session as any).mfa_status === MFAStatus.Pending

  const title = isMFA
    ? showMFAChoices
      ? 'Send verification code'
      : 'Enter code to continue'
    : (session as any)?.verify_message

  const MFAAction = (() => {
    if (selectedMFAMethod === MFAMethod.SMS) {
      return 'texted to you'
    }
    if (selectedMFAMethod === MFAMethod.Email) {
      return 'emailed to you'
    }
    if (selectedMFAMethod === MFAMethod.Voice) {
      return 'called you with'
    }
    return 'sent to you'
  })()

  const obfuscatePhone = (phone: string, countryCode: string): string => {
    const digits = phone.replace(/\D/g, '')
    const nationalNumber = digits.startsWith(countryCode) ? digits.slice(countryCode.length) : digits

    if (countryCode === '1') {
      // North American Numbering Plan
      return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 4)}** - **` + nationalNumber.slice(-2)
    }

    // International format
    return (
      `+${countryCode}-` + nationalNumber.slice(0, 4) + '*'.repeat(nationalNumber.length - 6) + nationalNumber.slice(-2)
    )
  }

  const updateData = (field: string, value: string) => {
    if (field in values) {
      setValues(prev => ({ ...prev, [field]: value }))
    }
  }

  const updateMFAChoices = useCallback(() => {
    const choices: MFAChoice[] = []

    if (session && (session as any).customer) {
      const customer = (session as any).customer

      if (customer.phone) {
        choices.push({
          value: MFAMethod.SMS,
          label: 'Text me at ' + obfuscatePhone(customer.phone, customer.phone_country_code),
        })
      }

      if (customer.email) {
        choices.push({
          value: MFAMethod.Email,
          label: 'Email me at ' + obfuscateEmail(customer.email),
        })
      }

      if ((session as any).mfa_method === MFAMethod.Email) {
        choices.reverse()
      }
    }

    setMFAChoices(choices)

    if (choices.length > 0) {
      setSelectedMFAMethod(choices[0].value)
    }
  }, [session])

  // MFA digit handling
  const handleDigitInput = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    if (mfaError) {
      setMfaError(false)
    }

    const value = event.target.value

    // Handle pasted content (multiple characters)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6)
      if (digits.length > 0) {
        const newDigits = [...mfaDigits]
        for (let i = 0; i < 6; i++) {
          newDigits[i] = digits[i] || ''
        }
        setMfaDigits(newDigits)

        // Focus appropriate input
        const nextEmptyIndex = newDigits.findIndex(digit => !digit)
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

        setTimeout(() => {
          mfaInputRefs.current[focusIndex]?.focus()
        }, 0)
      }
      return
    }

    if (value && /^\d$/.test(value)) {
      const newDigits = [...mfaDigits]
      newDigits[index] = value
      setMfaDigits(newDigits)

      // Move to next input
      if (index < 5) {
        mfaInputRefs.current[index + 1]?.focus()
      }
    } else if (value === '') {
      const newDigits = [...mfaDigits]
      newDigits[index] = ''
      setMfaDigits(newDigits)
    } else {
      // Invalid input, clear the field
      event.target.value = ''
    }
  }

  const handleKeyDown = async (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
      if (navigator.clipboard && navigator.clipboard.readText) {
        event.preventDefault()
        try {
          const text = await navigator.clipboard.readText()
          if (mfaError) {
            setMfaError(false)
          }

          const digits = text.replace(/\D/g, '').slice(0, 6)
          if (digits.length > 0) {
            const newDigits = [...mfaDigits]
            for (let i = 0; i < 6; i++) {
              newDigits[i] = digits[i] || ''
            }
            setMfaDigits(newDigits)

            const nextEmptyIndex = newDigits.findIndex(digit => !digit)
            const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

            setTimeout(() => {
              mfaInputRefs.current[focusIndex]?.focus()
            }, 0)
          }
        } catch {
          // fallback path: onPaste handler below will fire since we didn't preventDefault
        }
      }
      return
    }

    if (event.key === 'Backspace' && !mfaDigits[index] && index > 0) {
      mfaInputRefs.current[index - 1]?.focus()
    }

    if (!/^[0-9]$/.test(event.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault()
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()

    if (mfaError) {
      setMfaError(false)
    }

    const pastedData = event.clipboardData.getData('text/plain') || event.clipboardData.getData('text')
    const digits = pastedData.replace(/\D/g, '').slice(0, 6)

    if (digits.length > 0) {
      const newDigits = [...mfaDigits]
      for (let i = 0; i < 6; i++) {
        newDigits[i] = digits[i] || ''
      }
      setMfaDigits(newDigits)

      const nextEmptyIndex = newDigits.findIndex(digit => !digit)
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

      setTimeout(() => {
        mfaInputRefs.current[focusIndex]?.focus()
      }, 0)
    }
  }

  const clearMFADigits = () => {
    setMfaDigits(['', '', '', '', '', ''])
    setMfaError(false)
  }

  const selectMFAMethod = () => {
    clearMFADigits()
    updateMFAChoices()
    setShowMFAChoices(true)
  }

  const sendMFACode = async () => {
    if (!selectedMFAMethod || !session) {
      return
    }

    setProcessing(true)
    if (useGlobalLoading) {
      setLoading(true)
    } else {
      onVerifying?.(true)
    }

    try {
      const result = await authService.mfaSend(session.id, { method: selectedMFAMethod })
      if (result) {
        setSession(result)
        const nextMethod = (result as any)?.mfa_method
        if (nextMethod) {
          setSelectedMFAMethod(nextMethod)
        }
      }
      clearMFADigits()
      setShowMFAChoices(false)
    } catch (rawError: any) {
      const error = rawError?.originalError || rawError
      if (error?.response?.status === HTTP_STATUS.BAD_REQUEST) {
        const errors: string[] = []
        Object.keys(error.response.data).forEach(key => {
          errors.push(...error.response.data[key])
        })
        if (!useGlobalLoading) {
          errors.forEach(msg => toast.error(msg))
        } else {
          setMatchVerifyError(errors)
        }
      } else {
        setCriticalError(error)
      }
    } finally {
      setProcessing(false)
      if (useGlobalLoading) {
        setLoading(false)
      } else {
        onVerifying?.(false)
      }
    }
  }

  const setSession = useSessionStore(state => state.setSession)

  const initMFAState = useCallback(() => {
    if (
      session &&
      (session as any).status === SessionStatus.Verified &&
      (session as any).mfa_status === MFAStatus.Pending
    ) {
      if ((session as any).mfa_method) {
        setShowMFAChoices(false)
        setSelectedMFAMethod((session as any).mfa_method)
      } else {
        clearMFADigits()
        updateMFAChoices()
        setShowMFAChoices(true)
      }
    }
  }, [session, updateMFAChoices])

  const processPostMatch = async (sessionData?: any) => {
    if (sessionData) {
      setSession(sessionData)
    }
    if (getCart) {
      try {
        await cartService.getCart()
      } catch (error) {
        console.error('Failed to load cart:', error)
      }
    }
    initMFAState()
    clearProcessingMessages()
    onDone?.()
    if (!useGlobalLoading) {
      onVerifying?.(false)
    }
  }

  const verify = async () => {
    if (!session) {
      return
    }

    if (useGlobalLoading) {
      setLoading(true)
    } else {
      onVerifying?.(true)
    }
    emitProcessingMessages(CustomerVerifyMessages)

    const params = isMFA ? { code: mfaDigits.join('') } : values

    try {
      let result: any
      if (isMFA) {
        result = await authService.mfaVerify(session.id, params)
      } else {
        result = await authService.verify(session.id, params)
      }

      await processPostMatch(result)
    } catch (err: any) {
      const error = err?.originalError || err
      clearProcessingMessages()
      if (useGlobalLoading) {
        setLoading(false)
      }
      if (!useGlobalLoading) {
        onVerifying?.(false)
      }

      if (error?.response?.status === HTTP_STATUS.NOT_FOUND) {
        if (isMFA) {
          setMfaError(true)
        }

        const message = isMFA
          ? 'Sorry, but the code you entered is invalid.'
          : "Sorry, but we couldn't verify your information.<br/>Make sure you typed it correctly and it's the same as when you placed your order."
        if (useGlobalLoading) {
          setMatchVerifyError([message])
        } else {
          toast.error(message)
        }
      } else if (error?.response?.status === HTTP_STATUS.FORBIDDEN) {
        const message = error.response?.data?.message || "Sorry, but we couldn't verify your information."
        onFail?.(message)
      } else if (error?.response?.status === HTTP_STATUS.BAD_REQUEST) {
        const errors: string[] = []
        Object.keys(error.response.data).forEach(key => {
          errors.push(...error.response.data[key])
        })
        if (!useGlobalLoading) {
          errors.forEach(msg => toast.error(msg))
        } else {
          setMatchVerifyError(errors)
        }
      } else {
        setCriticalError(error)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setMatchVerifyError([])

    if (isMFA && showMFAChoices) {
      await sendMFACode()
    } else {
      await verify()
    }
  }

  const handleGoBack = () => {
    onBack?.()
  }

  useEffect(() => {
    if (isMFA) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Imperative MFA setup; nothing here is derivable from render inputs.
      initMFAState()
    }
  }, [session, isMFA, initMFAState])

  useEffect(() => {
    stopMouseflowRecording()
    return () => {
      clearProcessingMessages()
      setMatchVerifyError([])
    }
  }, [setMatchVerifyError])

  if (!session) {
    return null
  }

  return (
    <div className="w-full max-w-[560px] mx-auto px-2 py-6 lg:px-4">
      <StepTitle
        subtitle={
          isMFA ? (
            <div className="subtitle">
              {showMFAChoices
                ? 'Select how you would like to receive your verification code:'
                : selectedMFAMethod
                  ? `Please enter the 6-digit code we just ${MFAAction}`
                  : null}
            </div>
          ) : null
        }>
        <div dangerouslySetInnerHTML={{ __html: title }} />
      </StepTitle>

      {matchVerifyErrors?.length > 0 && (
        <div className="match-verify-alerts mt-4 space-y-2">
          {matchVerifyErrors.map((msg: string, idx: number) => (
            <div
              key={idx}
              role="alert"
              className="rounded-md px-4 py-3 text-sm border"
              style={{
                backgroundColor: 'color-mix(in srgb, #ef4444 8%, var(--content-background-color))',
                borderColor: 'color-mix(in srgb, #ef4444 35%, transparent)',
                color: '#b91c1c',
              }}
              dangerouslySetInnerHTML={{ __html: msg }}
            />
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        {isMFA ? (
          <>
            {showMFAChoices ? (
              <div className="mfa-choices">
                {MFAChoices.map(option => (
                  <label
                    key={option.value}
                    htmlFor={`option-${option.value}`}
                    className={cn('choice', { selected: option.value === selectedMFAMethod })}>
                    <input
                      type="radio"
                      id={`option-${option.value}`}
                      name="mfa-method"
                      value={option.value}
                      checked={selectedMFAMethod === option.value}
                      onChange={() => setSelectedMFAMethod(option.value)}
                      className="form-check-input"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            ) : selectedMFAMethod ? (
              <div className="mfa-code">
                <div className="group">
                  {[0, 1, 2].map(index => (
                    <input
                      key={`first-${index}`}
                      ref={el => (mfaInputRefs.current[index] = el)}
                      value={mfaDigits[index]}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      aria-label={`Digit ${index + 1}`}
                      className={cn('digit', { filled: !!mfaDigits[index], error: mfaError })}
                      onChange={e => handleDigitInput(index, e)}
                      onKeyDown={e => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                    />
                  ))}
                </div>
                <div className="dash">-</div>
                <div className="group">
                  {[3, 4, 5].map(index => (
                    <input
                      key={`second-${index}`}
                      ref={el => (mfaInputRefs.current[index] = el)}
                      value={mfaDigits[index]}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      aria-label={`Digit ${index + 1}`}
                      className={cn('digit', { filled: !!mfaDigits[index], error: mfaError })}
                      onChange={e => handleDigitInput(index, e)}
                      onKeyDown={e => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <>
            {(session as any).required_fields?.map((field: VerificationMethod, index: number) => (
              <VerificationField
                key={`verification-field-${index}`}
                method={field}
                value={values}
                placeholders={placeholders}
                onUpdateData={updateData}
                error={matchVerifyErrors?.length > 0}
                allFieldsRequired
              />
            ))}
            <input id="country" type="hidden" value={values.country} />
          </>
        )}

        <div className="flex justify-center gap-3 pt-2">
          {onBack && useGlobalLoading && (
            <button type="button" onClick={handleGoBack} className="btn-base back-btn w-1/2!">
              Back
            </button>
          )}

          <button
            type="submit"
            className={cn(
              'btn-base input-choice-btn input-btn flex! items-center justify-center gap-2',
              onBack && useGlobalLoading ? 'w-1/2!' : 'w-full'
            )}
            onClick={e => e.stopPropagation()}>
            {isMFA && showMFAChoices ? (
              'Send Code'
            ) : useGlobalLoading ? (
              'Submit'
            ) : (
              <>
                <span>Submit &amp; Continue</span>
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>

        {isMFA && !showMFAChoices && selectedMFAMethod && (
          <div className="pt-2">
            <button
              type="button"
              onClick={selectMFAMethod}
              disabled={processing}
              className="btn-base input-choice-btn input-btn w-full">
              Send a new code
            </button>
          </div>
        )}
      </form>

      {session.is_popup && !session.can_undo && (
        <button type="button" className="block mx-auto mt-4 btn-base btn-link" onClick={handleGoBack}>
          ×
        </button>
      )}
    </div>
  )
}

export default CustomerVerify
